import { Box, createStyles, Stack, Tabs } from '@mantine/core';
import { useApiGetPolicy, useApiListPolicyResults } from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import IconEmojiSelector from '@repo/common/components/IconEmojiSelector';
import { iconEmojiSelectorValueToString } from '@repo/common/components/IconEmojiSelector/utils';
import TabsList from '@repo/common/components/TabsList';
import { UserRole } from '@repo/common/enums/UserRole';
import { Badge } from '@repo/foundations';
import { useMount } from 'ahooks';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router';
import { useAuthUser, usePolicyEnabled, useWorkspace } from '../../api';
import { usePolicyWritePermission } from '../../api/hooks/policy';
import { AIAssistantSidebar } from '../../components/AIAssistant/AIAssistantSidebar/AIAssistantSidebar';
import DescriptionEditor from '../../components/DescriptionEditor';
import { DOCUMENTATION_WIDTH } from '../../components/Documentation/constants';
import { FeatureGuard } from '../../components/FeatureGuard/FeatureGuard';
import {
	PageLayoutContent,
	PageLayoutContentWrapper,
	PageLayoutOuterWrapper,
	PageLayoutWrapper,
} from '../../components/PageLayout';
import {
	RightSidebarMode,
	rightSidebarStore,
	RightSidebarWrapper,
} from '../../components/RightSidebar';
import { useFeatureFlags } from '../../utils/featureFlags';
import { useParamsIdSuffixUuid } from '../../utils/hook/utils';
import { useUpdatePolicy } from '../PoliciesPage/hooks';
import type { DjangoValueType } from '../TemplatePage/types';
import PolicyPageNavBar from './Navbar';
import PolicyPageSidebar from './PolicyPageSidebar';
import PolicyPageTitle from './PolicyPageTitle';
import { IgnoredResourcesTable } from './tabs/PolicyIgnoredResources/IgnoredResources';
import { PolicyIssues } from './tabs/PolicyIssues/PolicyIssues';
import PolicyOverview from './tabs/PolicyOverview/PolicyOverview';
import PolicyRemediation from './tabs/PolicyRemediation/PolicyRemediation';
import type { PolicyTab } from './types';

const useStyles = createStyles((theme) => ({
	wrapper: {
		margin: '0 auto',
		width: '100%',
		maxWidth: `${DOCUMENTATION_WIDTH}px`,
		paddingBottom: theme.spacing['5xl'],
	},
	content: {
		marginTop: 0,
		marginBottom: 0,
	},
	descriptionSpacer: {
		marginTop: theme.spacing.xs,
		marginBottom: theme.spacing.sm,
	},
	pageContent: {
		marginBottom: 0, // Override the default 2xl margin
	},
}));

function PolicyPage() {
	const id = useParamsIdSuffixUuid();
	const navigate = useNavigate();
	const { tab } = useParams();
	const { user } = useAuthUser();
	const { workspace } = useWorkspace();
	const { classes } = useStyles();

	const { data: policy } = useApiGetPolicy({
		pathParams: { policyId: id },
	});
	const { dataScanning } = useFeatureFlags();
	const { data: ignoredResults } = useApiListPolicyResults(
		{
			pathParams: { policyId: id },
			queryParams: {
				page: 1,
				ignored: true,
			},
		},
		{
			enabled: dataScanning,
		}
	);

	const hasWritePermission = usePolicyWritePermission();

	useMount(() => {
		trackEvent('policy/open', {}, user, workspace);
	});

	const { mutateAsync: updatePolicy } = useUpdatePolicy({});
	const handlePolicyChange = useCallback(
		(key: string) => async (value: DjangoValueType) => {
			await updatePolicy({
				pathParams: { policyId: id },
				body: { [key]: value },
			});
		},
		[id, updatePolicy]
	);

	const [activeTab, setActiveTab] = useState<PolicyTab>(
		(tab as PolicyTab) ?? 'overview'
	);
	const navigateToTab = useCallback(
		(t: PolicyTab) => {
			setActiveTab(t);
			navigate(`/policy/${id}/${t}`);
		},
		[id, navigate]
	);
	const handleTabChange = useCallback(
		(t: PolicyTab) => {
			setActiveTab(t);
			navigateToTab(t);
		},
		[navigateToTab]
	);

	const tabs = useMemo(
		() => [
			{ value: 'overview', label: 'Overview' },
			{ value: 'remediation', label: 'Remediation' },
			{
				value: 'issues',
				label: 'Issues',
				rightSection:
					(policy?.results_count ?? 0) > 0 ? (
						<Badge size="sm" variant="warning">
							{(policy?.results_count ?? 0).toLocaleString()}
						</Badge>
					) : undefined,
			},
			...(dataScanning &&
			ignoredResults?.results &&
			ignoredResults.results.length > 0
				? [{ value: 'ignored resources', label: 'Ignored resources' }]
				: []),
		],
		[dataScanning, ignoredResults?.results, policy]
	);

	const hasPolicyAccess = usePolicyEnabled();

	if (!policy) {
		return null;
	}

	return (
		<FeatureGuard
			v1AllowedRoles={[
				UserRole.ADMIN,
				UserRole.EDITOR,
				UserRole.VIEWER,
				UserRole.GUEST,
			]}
			v2Permission="Policies.Read"
			isFeatureIncludedInPlan={hasPolicyAccess}
			isAlwaysIncludedFeature={false}
			featureName="Policies"
			description="Create policies to ensure compliance and security across your data stack."
		>
			<PageLayoutOuterWrapper key={policy.id}>
				<Helmet>
					<title>{policy.name ?? 'Policy'}</title>
				</Helmet>
				<PageLayoutWrapper name="policies">
					<PageLayoutContentWrapper name="policies">
						<PolicyPageNavBar policy={policy} />
						<PageLayoutContent className={classes.pageContent}>
							<Stack className={classes.wrapper} spacing={0}>
								<Stack>
									<IconEmojiSelector
										value={
											policy.icon ??
											iconEmojiSelectorValueToString({
												iconName: 'fileCheck',
												iconColor: '#4a4a4a',
											})
										}
										onChange={handlePolicyChange('icon')}
										iconSize={28}
										readOnly={!hasWritePermission}
									/>
									<PolicyPageTitle
										policy={policy}
										readOnly={!hasWritePermission}
									/>
									<Box className={classes.descriptionSpacer}>
										<DescriptionEditor
											entityId={policy.id}
											readOnly={!hasWritePermission}
											value={policy?.description ?? ''}
											onChange={handlePolicyChange('description')}
											placeholder={'Policy description...'}
											withAi={false}
										/>
									</Box>
								</Stack>
								<Tabs value={activeTab} onTabChange={handleTabChange}>
									<TabsList tabs={tabs} />
									<Tabs.Panel value="overview">
										<PolicyOverview
											policy={policy}
											readOnly={!hasWritePermission}
										/>
									</Tabs.Panel>
									<Tabs.Panel value="remediation">
										<PolicyRemediation
											policy={policy}
											readOnly={!hasWritePermission}
										/>
									</Tabs.Panel>
									<Tabs.Panel value="issues">
										<PolicyIssues
											policy={policy}
											readOnly={!hasWritePermission}
										/>
									</Tabs.Panel>
									<Tabs.Panel value="ignored resources">
										<IgnoredResourcesTable
											ignoredResults={ignoredResults?.results ?? []}
											policy={policy}
											onTabChange={handleTabChange}
											readOnly={!hasWritePermission}
										/>
									</Tabs.Panel>
								</Tabs>
							</Stack>
						</PageLayoutContent>
					</PageLayoutContentWrapper>
					<RightSidebarWrapper>
						{rightSidebarStore.mode === RightSidebarMode.AI && (
							<AIAssistantSidebar />
						)}
						{rightSidebarStore.mode === RightSidebarMode.INFO && (
							<PolicyPageSidebar
								policy={policy}
								handlePolicyChange={handlePolicyChange}
								readOnly={!hasWritePermission}
							/>
						)}
					</RightSidebarWrapper>
				</PageLayoutWrapper>
			</PageLayoutOuterWrapper>
		</FeatureGuard>
	);
}

PolicyPage.displayName = 'PolicyPage';
const PolicyPageWithObserver = observer(PolicyPage);
export default PolicyPageWithObserver;
